import { CancelTokenSource } from "axios";
import axios from "services/http";
import {
  CdrDocumentFileContent,
  PatientCdrDocumentDto,
  PatientMedicalDocumentCommentDto,
  PatientMedicalDocumentHistoryDto,
  PdfResponse,
  TestResultDto,
  UpsertPatientMedicalDocumentCommentDto,
  UpsertPatientMedicalDocumentDto,
  VaccinationDto
} from "ts-types/api.types";

const { REACT_APP_API_BASE_URL: baseUrl } = process.env;
const testApiUri = `${baseUrl}/api/patient-record`;
const vaccinationApiUri = `${baseUrl}/api/vaccination-patient-record`;
const patientDocumentsApiUri = `${baseUrl}/api/patients`;

export const getVaccinationsForPatient = (
  patientId: number, source: CancelTokenSource)
  : Promise<VaccinationDto[]> => {

  return axios
    .get(`${vaccinationApiUri}/${patientId}/vaccination`, { cancelToken: source.token })
    .then((response) => response.data);
};

export const getVaccinationsForPatientClinicalDataRecord = (
  patientId: number, includingRevoked: boolean, source: CancelTokenSource)
  : Promise<VaccinationDto[]> => {

  return axios
    .get(`${testApiUri}/vaccination`, { params: { patientId, includingRevoked }, cancelToken: source.token })
    .then((response) => response.data);
};

export const revokeVaccination = (id: number, source: CancelTokenSource)
  : Promise<string> => {

  return axios
    .post(`${testApiUri}/vaccination/${id}/revoke`, { cancelToken: source.token })
    .then((response) => response.data);
};

export const getVaccinationInProgressForBooking = (
  bookingId: number, source: CancelTokenSource)
  : Promise<VaccinationDto> => {

  return axios
    .get(`${vaccinationApiUri}/${bookingId}/vaccination-in-progress`, { cancelToken: source.token })
    .then((response) => response.data);
};

export const getVaccinationCertificateForBooking = (
  bookingId: number, source: CancelTokenSource)
  : Promise<PdfResponse> => {

  return axios
    .get(`${vaccinationApiUri}/${bookingId}/certificate`, { cancelToken: source.token })
    .then((response) => response.data);
};

export const getTestResultsForPatient = (
  patientId: number, includingRevoked: boolean, source: CancelTokenSource)
  : Promise<TestResultDto[]> => {

  return axios
    .get(`${testApiUri}/test-results`, { params: { patientId, includingRevoked }, cancelToken: source.token })
    .then((response) => response.data);
};

export const revokeTestResult = (id: number, source: CancelTokenSource)
  : Promise<string> => {

  return axios
    .post(`${testApiUri}/test-results/${id}/revoke`, { cancelToken: source.token })
    .then((response) => response.data);
};

export const getTestCertificateUrl = (testCertDownloadKey: string, testCertName: string) => {
  return `${baseUrl}/9cce3855ce654aec9a418e505e28a878/${testCertDownloadKey}/${testCertName}`;
};

export const getPdfDocumentUrl = (
  patientDocumentDownloadKey: string,
  patientDocumentName: string) => {

  return `${baseUrl}/589s4f5d4g85f67gs3df451dfa8s79ws/${patientDocumentDownloadKey}/${patientDocumentName}`;
};

export const getPdfImageUrl = (
  patientDocumentDownloadKey: string,
  patientDocumentName: string,
  fileType: string) => {

  return `${baseUrl}/589s4f5d4g85f67gs3df451dfa8s79ws/${patientDocumentDownloadKey}/${patientDocumentName}/${fileType}`;
};

export const getMediaDocumentPreviewUrl = (
  mediaDocument: CdrDocumentFileContent): string => {

  const mediaType = mediaDocument.contentType.split("/")[1];

  return `${baseUrl}/9wo091s66e4588v3nw05bjg97r52jfx6/${mediaDocument.pdfId}/${mediaType}`;
};

export const getMediaDocumentDownloadUrl = (
  mediaDocument: CdrDocumentFileContent): string => {

  const mediaType = mediaDocument.contentType.split("/")[1];

  return `${baseUrl}/9wo091s66e4588v3nw05bjg97r52jfx6`
    + `/${mediaDocument.pdfId}/${mediaType}/${mediaDocument.pdfName}/download`;
};

export const getTestCertificateDownloadKey = (
  patientId: number,
  fileid: string,
  source: CancelTokenSource): Promise<PdfResponse> => {
  return axios.get(`${testApiUri}/${patientId}/${fileid}/download-key`, { cancelToken: source.token })
    .then(response => response.data);
};

export const getPatientDocuments = (
  patientId: number,
  includeArchived: boolean,
  requiresActionOnly: boolean,
  requiresDoctorActionOnly: boolean,
  abnormalFindingOnly: boolean,
  discussWithPatientOnly: boolean,
  procedureCreatedOnly: boolean,
  patientDocumentType: string | null,
  source: CancelTokenSource): Promise<PatientCdrDocumentDto[]> => {

  let params: any = {
    includeArchived: includeArchived,
    requiresActionOnly: requiresActionOnly,
    requiresDoctorActionOnly: requiresDoctorActionOnly,
    abnormalFindingOnly: abnormalFindingOnly,
    discussWithPatientOnly: discussWithPatientOnly,
    procedureCreatedOnly: procedureCreatedOnly
  };

  if (patientDocumentType != null && patientDocumentType != "") {
    params.patientDocumentType = patientDocumentType;
  }

  return axios.get(
      `${patientDocumentsApiUri}/${patientId}/patient-documents`,
      { params, cancelToken: source.token }
    )
    .then((response) => response.data);
};

export const getPatientDocument = (
  patientId: number,
  patientDocumentId: number,
  cancelTokenSource: CancelTokenSource): Promise<PatientCdrDocumentDto> => {

  return axios
    .get(
      `${patientDocumentsApiUri}/${patientId}/patient-documents/${patientDocumentId}`,
      { cancelToken: cancelTokenSource.token })
    .then(response => response.data);
};

export const getPatientMedicalDocumentComments = (
  patientMedicalDocumentId: number,
  archived: boolean,
  source: CancelTokenSource)
  : Promise<Array<PatientMedicalDocumentCommentDto>> => {

  return axios.get(
      `${patientDocumentsApiUri}/${patientMedicalDocumentId}/comments`,
      { params: { archived: archived }, cancelToken: source.token }
    )
    .then(response => response.data);
};

export const setPatientDocumentArchiveFlag = (
  patientId: number,
  documentId: number,
  type: string,
  archived: boolean,
  source: CancelTokenSource): Promise<boolean> => {

  let bodyFormData = new FormData();
  bodyFormData.append("type", type);
  bodyFormData.append("archived", "" + archived);

  return axios
    .post(
      `${patientDocumentsApiUri}/${patientId}/patient-documents/${documentId}/archived`,
      bodyFormData,
      { params: { documentId }, cancelToken: source.token })
    .then(response => response.data);
};

export const setRequiresActionFlag = (
  patientId: number,
  documentId: number,
  requiresAction: boolean,
  source: CancelTokenSource): Promise<boolean> => {

  let bodyFormData = new FormData();
  bodyFormData.append("requiresAction", "" + requiresAction);

  return axios
    .post(
      `${patientDocumentsApiUri}/${patientId}/patient-documents/${documentId}/requiresAction`,
      bodyFormData,
      { params: { documentId }, cancelToken: source.token })
    .then(response => response.data);
};

export const setRequiresDoctorActionFlag = (
  patientId: number,
  documentId: number,
  requiresAction: boolean,
  source: CancelTokenSource): Promise<boolean> => {

  let bodyFormData = new FormData();
  bodyFormData.append("requiresDoctorAction", "" + requiresAction);

  return axios
    .post(
      `${patientDocumentsApiUri}/${patientId}/patient-documents/${documentId}/requiresDoctorAction`,
      bodyFormData,
      { params: { documentId }, cancelToken: source.token })
    .then(response => response.data);
};

export const setAbnormalFindingFlag = (
  patientId: number,
  documentId: number,
  abnormalFinding: boolean,
  source: CancelTokenSource): Promise<boolean> => {

  let bodyFormData = new FormData();
  bodyFormData.append("abnormalFinding", "" + abnormalFinding);

  return axios
    .post(
      `${patientDocumentsApiUri}/${patientId}/patient-documents/${documentId}/abnormalFinding`,
      bodyFormData,
      { cancelToken: source.token })
    .then(response => response.data);
};

export const setDiscussWithPatientFlag = (
  patientId: number,
  documentId: number,
  discussWithPatient: boolean,
  source: CancelTokenSource
): Promise<boolean> => {

  let bodyFormData = new FormData();
  bodyFormData.append("discussWithPatient", "" + discussWithPatient);

  return axios
    .post(
      `${patientDocumentsApiUri}/${patientId}/patient-documents/${documentId}/discussWithPatient`,
      bodyFormData,
      { cancelToken: source.token }
    )
    .then(response => response.data);
};

export const setProcedureCreatedFlag = (
  patientId: number,
  documentId: number,
  procedureCreated: boolean,
  source: CancelTokenSource
): Promise<boolean> => {

  let bodyFormData = new FormData();
  bodyFormData.append("procedureCreated", "" + procedureCreated);

  return axios
    .post(
      `${patientDocumentsApiUri}/${patientId}/patient-documents/${documentId}/procedureCreated`,
      bodyFormData,
      { cancelToken: source.token }
    )
    .then(response => response.data);
};

export const setPatientDocumentSetPatientDocumentTextContent = (
  patientId: number,
  documentId: number,
  comments: UpsertPatientMedicalDocumentCommentDto[],
  source: CancelTokenSource): Promise<string> => {

  return axios
    .post(
      `${patientDocumentsApiUri}/${patientId}/patient-documents/${documentId}/comment`,
      comments,
      { cancelToken: source.token }
    )
    .then(response => response.data);
};

export const setPatientSynopsisReportControlledStatus = (
  documentId: number,
  patientId: number,
  comment: string,
  source: CancelTokenSource): Promise<string> => {

  let bodyFormData = new FormData();
  bodyFormData.append("comment", comment);

  return axios
    .post(
      `${patientDocumentsApiUri}/${patientId}/patient-documents/${documentId}/set-controlled-status`,
      bodyFormData,
      { cancelToken: source.token }
    )
    .then(response => response.data);
};

export const getUpsertPatientDocument = (
  patientId: number,
  patientDocumentId: number,
  cancelTokenSource: CancelTokenSource): Promise<UpsertPatientMedicalDocumentDto> => {

  return axios
    .get(`${patientDocumentsApiUri}/${patientId}/patient-documents/${patientDocumentId}/upsert`,
      { cancelToken: cancelTokenSource.token })
    .then(response => response.data);
};

export const getPatientDocumentHistory = (
  patientId: number,
  documentId: number,
  cancelTokenSource: CancelTokenSource): Promise<Array<PatientMedicalDocumentHistoryDto>> => {
  return axios
    .get(`${patientDocumentsApiUri}/${patientId}/patient-documents/${documentId}/history`,
      { params: { documentId }, cancelToken: cancelTokenSource.token })
    .then((response) => response.data);
};

export const getCdrDocumentFiles = (
  patientId: number,
  patientDocumentId: number,
  cancelTokenSource: CancelTokenSource,
  historyDocumentId?: number): Promise<CdrDocumentFileContent[]> => {

  const historyId = historyDocumentId ? historyDocumentId : "";

  return axios
    .get(`${patientDocumentsApiUri}/${patientId}/patient-documents/${patientDocumentId}/file`,
      { params: { historyDocumentId: historyId }, cancelToken: cancelTokenSource.token })
    .then((response) => response.data)
    .catch(error => {
      throw error;
    });
};

export const makeSynopsisReportCopy = (
  patientId: number,
  patientDocumentId: number,
  cancelTokenSource: CancelTokenSource): Promise<CdrDocumentFileContent> => {

  return axios
    .get(`${patientDocumentsApiUri}/${patientId}/patient-documents/${patientDocumentId}/copy/pdf`,
      { cancelToken: cancelTokenSource.token })
    .then((response) => response.data)
    .catch(error => {
      throw error;
    });
};

export const getPatientDocumentText = (
  patientId: number,
  patientDocumentId: number,
  cancelTokenSource: CancelTokenSource,
  historyDocumentId?: number): Promise<string> => {

  const historyId = historyDocumentId ? historyDocumentId : "";

  return axios
    .get(`${patientDocumentsApiUri}/${patientId}/patient-documents/${patientDocumentId}/text`,
      { params: { historyDocumentId: historyId }, cancelToken: cancelTokenSource.token })
    .then((response) => response.data);
};
